// src/pages/user/UserChildren.tsx
// Route: /user/children

import { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useGetChildrenQuery } from "../../app/api/parentApi";
import { useInitializePaymentMutation } from "../../app/api/paymentApi";
import { useAddChildMutation } from "../../app/api/authApi";
import { appendChild } from "../../app/features/authSlice";
import type { ChildCard } from "../../app/api/parentApi";
import type { AddChildRequest } from "../../app/api/authApi";

import { ArrowRight, BookOpen, CalendarDays, Check, CreditCard, Lightbulb, TriangleAlert, User } from "lucide-react";
/* ─── CONSTANTS ──────────────────────────────────────────────────── */

// eslint-disable-next-line no-useless-escape
const EMAIL_RE = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

const TRACK_SHORT: Record<string, string> = {
  foundation_builders: "Foundation (6–9)",
  explorers: "Explorers (9–12)",
  innovators: "Innovators (13–18)",
  unassigned: "Unassigned",
};

const TRACK_COLORS: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  foundation_builders: { bg: "#fff4f1", text: "#7a3020", border: "#f5c5b0" },
  explorers: { bg: "#ebeff7", text: "#4a44b0", border: "#cdd4e2" },
  innovators: { bg: "#e8ecf5", text: "#1b2d5e", border: "#c9cfe0" },
  unassigned: { bg: "#f1f3f8", text: "#8189a0", border: "#dde2ec" },
};

const STATUS_MAP: Record<string, { label: string; cls: string }> = {
  prospect: {
    label: "Prospect",
    cls: "bg-[#f1f3f8] text-[#8189a0] border border-[#dde2ec]",
  },
  enrolled: {
    label: "Enrolled",
    cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",
  },
  active: {
    label: "Active",
    cls: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]",
  },
  inactive: {
    label: "Inactive",
    cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
  },
};

const PAY_STATUS_MAP: Record<string, { label: string; cls: string }> = {
  paid: {
    label: "Paid",
    cls: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]",
  },
  pending: {
    label: "Pending",
    cls: "bg-[#fff8ee] text-[#7a4a10] border border-[#f5d9a0]",
  },
  overdue: {
    label: "Overdue",
    cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
  },
  waived: {
    label: "Waived",
    cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",
  },
};

const ENROL_STATUS_MAP: Record<string, { label: string; cls: string }> = {
  pending: {
    label: "Pending payment",
    cls: "bg-[#fff8ee] text-[#7a4a10] border border-[#f5d9a0]",
  },
  confirmed: {
    label: "Confirmed",
    cls: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]",
  },
  completed: {
    label: "Completed",
    cls: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",
  },
  withdrawn: {
    label: "Withdrawn",
    cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
  },
};

const BILLING_CONFIG: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  monthly: { bg: "#fff8ee", text: "#7a4a10", border: "#f5d9a0" },
  quarterly: { bg: "#ebeff7", text: "#4a44b0", border: "#cdd4e2" },
  annually: { bg: "#edfaf4", text: "#2a7a56", border: "#b6e8d0" },
};

/* ─── HELPERS ────────────────────────────────────────────────────── */

function ageFromDOB(dob: string): number {
  return Math.floor(
    (Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25),
  );
}
function deriveTrackLabel(dob: string): string {
  if (!dob) return "";
  const age = ageFromDOB(dob);
  if (age >= 6 && age <= 9) return "Foundation Builders (Ages 6–9)";
  if (age > 9 && age <= 12) return "Explorers (Ages 9–12)";
  if (age > 12 && age <= 18) return "Innovators (Ages 13–18)";
  return age < 6 ? "Too young (minimum age: 6)" : "Too old (maximum age: 18)";
}
function isValidAge(dob: string): boolean {
  if (!dob) return false;
  const age = ageFromDOB(dob);
  return age >= 6 && age <= 18;
}

/* ─── ATOMS ──────────────────────────────────────────────────────── */

function Chip({ label, cls }: { label: string; cls: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}
function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-[#ebeff7]">
      <div
        className="h-full rounded-full bg-[#1b2d5e] transition-all duration-700"
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}
function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-2xl bg-[#ebeff7] ${className}`} />
  );
}
function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
      />
    </svg>
  );
}

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
  max?: string;
}
function Field({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  error,
  hint,
  disabled,
  required,
  max,
}: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-semibold text-[#3a3750]">
        {label}
        {!required && (
          <span className="ml-1.5 text-[12.5px] font-normal text-[#a6adbf]">
            (optional)
          </span>
        )}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        max={max}
        className={[
          "w-full rounded-2xl border bg-white px-4 py-3.5 text-sm text-[#16213e] placeholder:text-[#a6adbf] outline-none transition-all focus:ring-4 disabled:cursor-not-allowed disabled:opacity-50",
          error
            ? "border-[#e57373] focus:border-[#e57373] focus:ring-[#e57373]/10"
            : "border-[#dde2ec] focus:border-[#1b2d5e] focus:ring-[#1b2d5e]/12",
        ].join(" ")}
      />
      {hint && !error && <p className="text-[12.5px] text-[#8189a0]">{hint}</p>}
      {error && (
        <p className="flex items-center gap-1.5 text-[12.5px] text-[#e57373]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#e57373]" />
          {error}
        </p>
      )}
    </div>
  );
}

/* ─── ADD CHILD DRAWER ───────────────────────────────────────────── */

function AddChildDrawer({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (c: any) => void;
}) {
  const [addChild, { isLoading }] = useAddChildMutation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState(false);

  const trackLabel = dob ? deriveTrackLabel(dob) : "";
  const ageOk = dob ? isValidAge(dob) : true;
  const computedAge = dob ? ageFromDOB(dob) : null;

  const reset = () => {
    setFirstName("");
    setLastName("");
    setDob("");
    setEmail("");
    setErrors({});
    setServerError("");
    setSuccess(false);
  };
  const handleClose = () => {
    reset();
    onClose();
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!firstName.trim()) e.firstName = "First name is required";
    if (!lastName.trim()) e.lastName = "Last name is required";
    if (!dob) e.dob = "Date of birth is required";
    else if (!isValidAge(dob)) e.dob = "Age must be between 6 and 18";
    if (email && !EMAIL_RE.test(email.trim()))
      e.email = "Enter a valid email address";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    const payload: AddChildRequest = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth: dob,
      ...(email ? { email: email.trim().toLowerCase() } : {}),
    };
    try {
      const result = await addChild(payload).unwrap();
      setSuccess(true);
      setTimeout(() => {
        onSuccess(result.data.child);
        handleClose();
      }, 1400);
    } catch (err: any) {
      setServerError(
        err?.data?.error || "Failed to add child. Please try again.",
      );
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[440px]"
        role="dialog"
        aria-modal
        aria-label="Add a child"
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <div>
            <h2 className="text-lg font-bold tracking-[-0.03em] text-[#16213e]">
              Add a child
            </h2>
            <p className="mt-0.5 text-xs text-[#8189a0]">
              Programme track is set automatically from date of birth.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8]"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {success ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#edfaf4] text-3xl">
              <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
            </span>
            <p className="text-lg font-bold text-[#16213e]">
              {firstName} has been added!
            </p>
            <p className="max-w-xs text-sm text-[#5f6780]">
              They appear as a <strong>Prospect</strong>. Complete enrolment to
              confirm their place.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            noValidate
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {serverError && (
                <div className="flex items-start gap-3 rounded-2xl border border-[#fde0d9] bg-[#fff5f3] px-4 py-3.5">
                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#efb0a4] text-[11.5px] font-bold text-white">
                    !
                  </span>
                  <p className="text-sm text-[#b94a3a]">{serverError}</p>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  id="add-firstName"
                  label="First name"
                  value={firstName}
                  onChange={(v) => {
                    setFirstName(v);
                    setErrors((p) => ({ ...p, firstName: "" }));
                  }}
                  placeholder="e.g. Adaeze"
                  error={errors.firstName}
                  disabled={isLoading}
                  required
                />
                <Field
                  id="add-lastName"
                  label="Last name"
                  value={lastName}
                  onChange={(v) => {
                    setLastName(v);
                    setErrors((p) => ({ ...p, lastName: "" }));
                  }}
                  placeholder="e.g. Okonkwo"
                  error={errors.lastName}
                  disabled={isLoading}
                  required
                />
              </div>
              <div>
                <Field
                  id="add-dob"
                  label="Date of birth"
                  type="date"
                  value={dob}
                  onChange={(v) => {
                    setDob(v);
                    setErrors((p) => ({ ...p, dob: "" }));
                  }}
                  error={errors.dob}
                  disabled={isLoading}
                  max={new Date().toISOString().split("T")[0]}
                  required
                />
                {trackLabel && (
                  <div className="mt-2 flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12.5px] font-semibold ${ageOk ? "border border-[#cdd4e2] bg-[#ebeff7] text-[#4a44b0]" : "border border-[#f5c0c0] bg-[#fff3f3] text-[#9b2a2a]"}`}
                    >
                      {ageOk ? <BookOpen className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> : <TriangleAlert className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />} {trackLabel}
                    </span>
                    {ageOk && computedAge !== null && (
                      <span className="text-[12.5px] text-[#8189a0]">
                        Age {computedAge}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <Field
                id="add-email"
                label="Child's email"
                type="email"
                value={email}
                onChange={(v) => {
                  setEmail(v);
                  setErrors((p) => ({ ...p, email: "" }));
                }}
                placeholder="only if they have one"
                hint="Leave blank for young children — you manage their account."
                error={errors.email}
                disabled={isLoading}
              />
              <div className="rounded-[18px] border border-[#dde2ec] bg-[#f9fafc] px-4 py-4">
                <p className="text-[12.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
                  What happens next
                </p>
                <ul className="mt-2 space-y-1.5 text-xs leading-5 text-[#5a6278]">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#1b2d5e]" />
                    Added as a <strong>Prospect</strong> — not yet confirmed.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#1b2d5e]" />
                    Track auto-assigned from their age.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#1b2d5e]" />
                    Status moves to <strong>Enrolled</strong> once payment is
                    confirmed.
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex flex-shrink-0 gap-3 border-t border-[#ebeff7] px-6 py-5">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1 rounded-2xl border border-[#dde2ec] py-3 text-sm font-semibold text-[#5a6278] hover:bg-[#f1f3f8] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || (dob !== "" && !ageOk)}
                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-[#1b2d5e] py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.30)] hover:bg-[#24397a] disabled:opacity-60"
              >
                {isLoading ? (
                  <>
                    <Spinner />
                    Adding…
                  </>
                ) : (
                  "Add child"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}

/* ─── CHILD DETAIL CARD ──────────────────────────────────────────── */

function ChildDetailCard({ child }: { child: ChildCard }) {
  const [initializePayment, { isLoading: payLoading }] =
    useInitializePaymentMutation();
  const tc = TRACK_COLORS[child.track] ?? TRACK_COLORS.unassigned;
  const statusInfo = STATUS_MAP[child.studentStatus] ?? STATUS_MAP.prospect;
  const payInfo = PAY_STATUS_MAP[child.paymentStatus] ?? PAY_STATUS_MAP.pending;
  const enrolInfo = child.enrolmentStatus
    ? ENROL_STATUS_MAP[child.enrolmentStatus]
    : null;
  const billingCfg = child.billingPeriod
    ? BILLING_CONFIG[child.billingPeriod]
    : null;
  const initials =
    `${child.firstName.charAt(0)}${child.lastName.charAt(0)}`.toUpperCase();

  const handlePayNow = async () => {
    if (!child.enrolmentId) return;
    try {
      const result = await initializePayment({
        enrolmentId: child.enrolmentId,
      }).unwrap();
      window.location.href = result.data.authorizationUrl;
    } catch (err) {
      console.error("Payment initiation failed:", err);
    }
  };

  return (
    <div className="rounded-[28px] border border-[#e5e8ef] bg-white shadow-[0_4px_20px_rgba(27,45,94,0.06)] transition hover:shadow-[0_8px_32px_rgba(27,45,94,0.10)]">
      {/* Track accent strip */}
      <div className="h-1 rounded-t-[28px]" style={{ background: tc.border }} />

      {/* Header */}
      <div className="flex items-start gap-4 p-5">
        <span
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[14px] text-base font-bold"
          style={{
            background: tc.bg,
            color: tc.text,
            border: `1px solid ${tc.border}`,
          }}
        >
          {initials}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold tracking-[-0.02em] text-[#16213e]">
            {child.firstName} {child.lastName}
          </p>
          <p className="text-xs text-[#8189a0]">Age {child.age}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
              style={{
                background: tc.bg,
                color: tc.text,
                border: `1px solid ${tc.border}`,
              }}
            >
              {TRACK_SHORT[child.track] ?? child.track}
            </span>
            <Chip label={statusInfo.label} cls={statusInfo.cls} />
          </div>
        </div>
        <Chip label={payInfo.label} cls={payInfo.cls} />
      </div>

      {/* Enrolment status block — the key new section */}
      {child.enrolmentId ? (
        <div className="mx-5 mb-3 rounded-[18px] border border-[#ebeff7] bg-[#f9fafc] overflow-hidden">
          {/* Enrolment header row */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#ebeff7]">
            <div className="flex items-center gap-2">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Enrolment
              </p>
              {child.quarter && (
                <span className="text-[11.5px] font-semibold text-[#5a6278]">
                  {child.quarter}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {/* Billing plan chip */}
              {billingCfg && (
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-bold"
                  style={{
                    background: billingCfg.bg,
                    color: billingCfg.text,
                    border: `1px solid ${billingCfg.border}`,
                  }}
                >
                  {child.billingLabel}
                </span>
              )}
              {/* Enrolment status chip */}
              {enrolInfo && (
                <Chip label={enrolInfo.label} cls={enrolInfo.cls} />
              )}
            </div>
          </div>

          {/* Progress */}
          <div className="px-4 py-3">
            <div className="mb-1.5 flex items-center justify-between text-[11.5px]">
              <span className="font-semibold text-[#5a6278]">
                Week {child.week ?? "—"}
                {child.currentProject ? ` · ${child.currentProject}` : ""}
              </span>
              <span className="font-bold text-[#1b2d5e]">
                {child.progress}%
              </span>
            </div>
            <ProgressBar pct={child.progress} />
          </div>

          {/* Next payment date — only for confirmed enrolments */}
          {child.nextPaymentLabel && child.enrolmentStatus === "confirmed" && (
            <div className="flex items-center gap-2 border-t border-[#ebeff7] px-4 py-2.5">
              <CalendarDays className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              <p className="text-[12.5px] text-[#5a6278]">
                Next payment:{" "}
                <span className="font-semibold text-[#16213e]">
                  {child.nextPaymentLabel}
                </span>
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="mx-5 mb-3">
          <div className="rounded-[14px] border border-dashed border-[#cdd4e2] px-4 py-3 text-center">
            <p className="text-xs font-medium text-[#8189a0]">
              Not enrolled in an active quarter
            </p>
          </div>
        </div>
      )}

      {/* Next session */}
      {child.nextSession && (
        <div className="mx-5 mb-3 flex items-center gap-2.5 rounded-[14px] border border-[#ebeff7] bg-[#f9fafc] px-3.5 py-2.5">
          <CalendarDays className="h-4 w-4 flex-shrink-0" aria-hidden />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#8189a0]">
              Next session
            </p>
            <p className="text-xs font-bold text-[#16213e]">
              {child.nextSession}
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2.5 border-t border-[#f7f8fb] px-5 py-4">
        {child.paymentStatus === "pending" && child.enrolmentId && (
          <button
            onClick={handlePayNow}
            disabled={payLoading}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-2xl bg-[#1b2d5e] py-2.5 text-xs font-semibold text-white shadow-[0_4px_16px_rgba(27,45,94,0.28)] hover:bg-[#24397a] disabled:opacity-50"
          >
            {payLoading ? (
              <>
                <Spinner />
                Processing…
              </>
            ) : (
              <><CreditCard className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Pay now</>
            )}
          </button>
        )}
        <Link
          to={`/user/children/${child.id}`}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-2xl border border-[#dde2ec] py-2.5 text-xs font-semibold text-[#5a6278] hover:border-[#c9cfe0] hover:bg-[#f1f3f8]"
        >
          View profile{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </Link>
      </div>
    </div>
  );
}

/* ─── SKELETON ───────────────────────────────────────────────────── */

function ChildCardSkeleton() {
  return (
    <div className="rounded-[28px] border border-[#e5e8ef] bg-white p-5 shadow-[0_4px_20px_rgba(27,45,94,0.06)]">
      <div className="flex items-start gap-4">
        <Skeleton className="h-12 w-12 flex-shrink-0 rounded-[14px]" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="h-3 w-1/4" />
          <div className="flex gap-1.5 pt-1">
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        </div>
      </div>
      <Skeleton className="mt-4 h-[88px] rounded-[18px]" />
      <div className="mt-4 flex gap-2.5 border-t border-[#f7f8fb] pt-4">
        <Skeleton className="h-9 flex-1 rounded-2xl" />
        <Skeleton className="h-9 flex-1 rounded-2xl" />
      </div>
    </div>
  );
}

/* ─── SUMMARY BAR ────────────────────────────────────────────────── */

function SummaryBar({ children }: { children: ChildCard[] }) {
  const items = [
    { label: "Total", value: children.length, color: "#1b2d5e" },
    {
      label: "Active",
      value: children.filter((c) => c.studentStatus === "active").length,
      color: "#6cc2a0",
    },
    {
      label: "Enrolled",
      value: children.filter((c) => c.studentStatus === "enrolled").length,
      color: "#4a44b0",
    },
    {
      label: "Prospects",
      value: children.filter((c) => c.studentStatus === "prospect").length,
      color: "#f0a050",
    },
    {
      label: "Fees due",
      value: children.filter(
        (c) => c.paymentStatus === "pending" && c.enrolmentId,
      ).length,
      color: "#e57373",
    },
  ];
  return (
    <div className="grid grid-cols-5 gap-3">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-[18px] border border-[#e5e8ef] bg-white px-4 py-3.5 text-center shadow-[0_2px_8px_rgba(27,45,94,0.04)]"
        >
          <p
            className="text-2xl font-bold tracking-[-0.04em]"
            style={{ color: item.color }}
          >
            {item.value}
          </p>
          <p className="mt-0.5 text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
            {item.label}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ─── FILTER BAR ─────────────────────────────────────────────────── */

type FilterStatus = "all" | "active" | "enrolled" | "prospect" | "inactive";
type FilterTrack = "all" | "foundation_builders" | "explorers" | "innovators";
type SortKey = "name" | "age" | "status" | "progress";
interface Filters {
  status: FilterStatus;
  track: FilterTrack;
  sort: SortKey;
}

function FilterBar({
  filters,
  onChange,
  totalCount,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  totalCount: number;
}) {
  const sel =
    "rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs font-semibold text-[#3b4560] outline-none focus:border-[#1b2d5e] cursor-pointer";
  return (
    <div className="flex flex-wrap items-center gap-3">
      <p className="text-sm font-semibold text-[#16213e]">
        {totalCount} {totalCount === 1 ? "child" : "children"}
      </p>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <select
          value={filters.status}
          onChange={(e) =>
            onChange({ ...filters, status: e.target.value as FilterStatus })
          }
          className={sel}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="enrolled">Enrolled</option>
          <option value="prospect">Prospect</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={filters.track}
          onChange={(e) =>
            onChange({ ...filters, track: e.target.value as FilterTrack })
          }
          className={sel}
        >
          <option value="all">All tracks</option>
          <option value="foundation_builders">Foundation (6–9)</option>
          <option value="explorers">Explorers (9–12)</option>
          <option value="innovators">Innovators (13–18)</option>
        </select>
        <select
          value={filters.sort}
          onChange={(e) =>
            onChange({ ...filters, sort: e.target.value as SortKey })
          }
          className={sel}
        >
          <option value="name">Sort: Name</option>
          <option value="age">Sort: Age</option>
          <option value="status">Sort: Status</option>
          <option value="progress">Sort: Progress</option>
        </select>
      </div>
    </div>
  );
}

/* ─── ROOT PAGE ──────────────────────────────────────────────────── */

export default function UserChildren() {
  const dispatch = useDispatch();
  const { data, isLoading, isError, refetch } = useGetChildrenQuery(undefined, {
    // Re-fetch every 60s so progress bar updates when reports are published
    pollingInterval: 60000,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    status: "all",
    track: "all",
    sort: "name",
  });

  const allChildren: ChildCard[] = data?.data ?? [];
  const filtered = allChildren
    .filter(
      (c) => filters.status === "all" || c.studentStatus === filters.status,
    )
    .filter((c) => filters.track === "all" || c.track === filters.track)
    .sort((a, b) => {
      switch (filters.sort) {
        case "age":
          return (a.age ?? 0) - (b.age ?? 0);
        case "status":
          return a.studentStatus.localeCompare(b.studentStatus);
        case "progress":
          return (b.progress ?? 0) - (a.progress ?? 0);
        default:
          return a.firstName.localeCompare(b.firstName);
      }
    });

  const handleChildAdded = (newChild: any) => {
    dispatch(appendChild(newChild));
    refetch();
  };

  if (isError)
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm font-semibold text-[#16213e]">
          Couldn't load your children
        </p>
        <button
          onClick={refetch}
          className="rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-xs font-semibold text-white"
        >
          Retry
        </button>
      </div>
    );

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[1.6rem] font-bold tracking-[-0.04em] text-[#16213e]">
            My Children
          </h1>
          <p className="mt-1 text-sm text-[#5f6780]">
            Manage your children's profiles, enrolments, and payments.
          </p>
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          className="inline-flex flex-shrink-0 items-center gap-2 self-start rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.30)] hover:bg-[#24397a]"
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 3v10M3 8h10"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
          Add a child
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[68px] rounded-[18px]" />
          ))}
        </div>
      ) : allChildren.length > 0 ? (
        <SummaryBar children={allChildren} />
      ) : null}

      {!isLoading && allChildren.length > 0 && (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="text-[12.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
            Tracks
          </span>
          {(["foundation_builders", "explorers", "innovators"] as const).map(
            (t) => {
              const c = TRACK_COLORS[t];
              const count = allChildren.filter((ch) => ch.track === t).length;
              if (!count) return null;
              return (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12.5px] font-semibold"
                  style={{
                    background: c.bg,
                    color: c.text,
                    border: `1px solid ${c.border}`,
                  }}
                >
                  {TRACK_SHORT[t]} · {count}
                </span>
              );
            },
          )}
        </div>
      )}

      {!isLoading && allChildren.length > 0 && (
        <div className="mt-5">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            totalCount={filtered.length}
          />
        </div>
      )}

      <div className="mt-6">
        {isLoading ? (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <ChildCardSkeleton key={i} />
            ))}
          </div>
        ) : allChildren.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ebeff7] text-3xl">
              <User className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
            </span>
            <h3 className="mt-5 text-lg font-bold text-[#16213e]">
              No children added yet
            </h3>
            <p className="mt-2 max-w-xs text-sm leading-6 text-[#5f6780]">
              Add your first child to get started. Their programme track is
              automatically assigned from their age.
            </p>
            <button
              onClick={() => setDrawerOpen(true)}
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-[#1b2d5e] px-6 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.30)] hover:bg-[#24397a]"
            >
              + Add your first child
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-[24px] border border-[#e5e8ef] bg-[#f9fafc] py-12 text-center">
            <p className="text-sm font-semibold text-[#16213e]">
              No children match this filter
            </p>
            <button
              onClick={() =>
                setFilters({ status: "all", track: "all", sort: "name" })
              }
              className="mt-3 text-xs font-semibold text-[#1b2d5e]"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((child) => (
              <ChildDetailCard key={child.id} child={child} />
            ))}
          </div>
        )}
      </div>

      {!isLoading && allChildren.length > 0 && (
        <div className="mt-8 rounded-[24px] bg-[#16213e] px-6 py-5 text-white">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-bold">
                <Lightbulb className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Every showcase, ask your child
              </p>
              <p className="mt-1 text-xs leading-5 text-white/65">
                "What did you make? Who does it help? What did you learn?" — the
                formation of an innovator.
              </p>
            </div>
            <Link
              to="/user/showcases"
              className="flex-shrink-0 rounded-2xl border border-white/20 px-5 py-2.5 text-xs font-semibold text-white/80 hover:bg-white/10"
            >
              View showcases{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
            </Link>
          </div>
        </div>
      )}

      <AddChildDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={handleChildAdded}
      />
    </>
  );
}
